import { BallIcon } from '@/common/assets';
import { isNil, didTeamWin } from '@/common/utils';

import formatDateLocaleDependent from '../../../utils/formatDateLocaleDependent';
import type { LatestGame } from '../LastFiveGames.types';

import GameScore from './GameScore';
import PenaltyScore from './PenaltyScore';
import GoalScorers from './GoalScorers';

export type GameRowProps = LatestGame;

const getTeamNameTextColorClass = (didTeamWin: boolean) => {
  if (didTeamWin) {
    return 'text-pmw-label-primary-light dark:text-pmw-label-primary-dark';
  }

  return 'text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark';
};

export default function GameRow({
  date,
  league,
  leagueFlag,
  stage,
  stageNumber,
  homeTeam,
  awayTeam,
  homeScore,
  awayScore,
  homePenaltyScore,
  awayPenaltyScore,
  result,
  homeGoalScorers,
  awayGoalScorers,
}: GameRowProps) {
  const formattedDate = formatDateLocaleDependent(date);

  const didHomeTeamWin = didTeamWin({
    teamScore: homeScore,
    opponentScore: awayScore,
    teamPenaltyScore: homePenaltyScore || 0,
    opponentPenaltyScore: awayPenaltyScore || 0,
  });
  const didAwayTeamWin = didTeamWin({
    teamScore: awayScore,
    opponentScore: homeScore,
    teamPenaltyScore: awayPenaltyScore || 0,
    opponentPenaltyScore: homePenaltyScore || 0,
  });

  const hasGoalScorers = homeGoalScorers.length > 0 || awayGoalScorers.length > 0;
  const hasPenaltyScore = homePenaltyScore !== null && awayPenaltyScore !== null;

  const teamNameCommonClasses =
    'max-w-full truncate font-robotoMedium font-medium text-sm leading-[20px]';

  const getLeagueString = () => {
    if (!stage) {
      return league;
    }

    if (isNil(stageNumber)) {
      return `${league} - ${stage}`;
    }

    return `${league} - ${stage} ${stageNumber}`;
  };

  return (
    <div className="flex flex-col gap-[8px] p-2 [&+&]:border-t border-pmw-separator-light dark:border-pmw-separator-dark">
      <div className="flex justify-between items-center gap-1">
        <div className="flex items-center gap-1 min-w-0">
          {leagueFlag && (
            <img className="w-[12px] h-[12px] p-px" src={leagueFlag} alt={`${league} flag`} />
          )}
          <span className="truncate font-robotoRegular font-normal text-[10px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
            {getLeagueString()}
          </span>
        </div>
        <span className="px-1 rounded-lg font-robotoMedium font-medium text-[10px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark bg-pmw-date-pill-bg-light dark:bg-pmw-date-pill-bg-dark">
          {formattedDate}
        </span>
      </div>
      <div className="grid grid-cols-[[home]_1fr_[score]_auto_[away]_1fr] justify-items-center gap-1">
        <span
          className={`justify-self-end self-center text-end ${teamNameCommonClasses} ${getTeamNameTextColorClass(didHomeTeamWin)}`}
        >
          {homeTeam}
        </span>
        <GameScore
          className="self-center"
          homeScore={homeScore}
          awayScore={awayScore}
          result={result}
        />
        <span
          className={`justify-self-start self-center ${teamNameCommonClasses} ${getTeamNameTextColorClass(didAwayTeamWin)}`}
        >
          {awayTeam}
        </span>
        {hasPenaltyScore && (
          <PenaltyScore
            className="col-start-[score] my-0.5"
            homePenaltyScore={homePenaltyScore!}
            awayPenaltyScore={awayPenaltyScore!}
          />
        )}
        {hasGoalScorers && (
          <>
            <GoalScorers
              className="col-start-[home] justify-self-end text-end"
              goalScorers={homeGoalScorers}
            />
            <div className="flex items-center h-[16px]">
              <BallIcon className="text-pmw-if-secondary-light dark:text-pmw-if-secondary-dark" />
            </div>
            <GoalScorers className="justify-self-start" goalScorers={awayGoalScorers} />
          </>
        )}
      </div>
    </div>
  );
}
